import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Employee {
  id: string
  employee_id: string
  first_name: string
  last_name: string
  department: string
  position: string
  status: string
  employment_type: string
  hire_date: string
}

interface Department {
  id: string
  name: string
  head: string
  headcount: number
  budget: number
}

export default function HRPage() {
  const [employees, setEmployees] = useState<Employee[]>([])
  const [departments, setDepartments] = useState<Department[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [empRes, deptRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/hr/employees'),
      fetch(API_BASE + '/hr/departments'),
      fetch(API_BASE + '/hr/statistics')
    ])
    if (empRes.ok) setEmployees((await empRes.json()).employees || [])
    if (deptRes.ok) setDepartments((await deptRes.json()).departments || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'on_leave': return 'bg-yellow-100 text-yellow-800'
      case 'probation': return 'bg-blue-100 text-blue-800'
      case 'terminated': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">HR Management</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Add Employee</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Employees</div>
              <div className="text-2xl font-bold">{statistics.total_employees}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active</div>
              <div className="text-2xl font-bold text-green-600">{statistics.active_employees}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">On Leave</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.on_leave}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Departments</div>
              <div className="text-2xl font-bold">{statistics.departments_count}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Salary</div>
              <div className="text-2xl font-bold text-blue-600">{formatCurrency(statistics.avg_salary)}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Employees</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {employees.slice(0, 12).map(emp => (
                  <tr key={emp.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{emp.first_name} {emp.last_name}</div>
                      <div className="text-xs text-gray-500">{emp.employee_id} • {emp.position}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm">{emp.department}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm capitalize">{emp.employment_type.replace(/_/g, ' ')}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(emp.status)}`}>
                        {emp.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Departments</h3>
            <div className="space-y-3">
              {departments.map(dept => (
                <div key={dept.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm">{dept.name}</span>
                    <span className="text-sm font-bold">{dept.headcount}</span>
                  </div>
                  <div className="text-xs text-gray-500">Head: {dept.head}</div>
                  <div className="text-xs text-blue-600 mt-1">Budget: {formatCurrency(dept.budget)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
